import { BLOCKED_SLUGS } from '../config';
import type { AnalyticsFile, Entry, RawIndexEntry, RepoMeta } from '../types';
import { hasIcon } from '../ui/icon';
import { metaFor, resolveCategory } from './categories';

const TYPES = new Set(['tool', 'web', 'app', 'game']);

const BLOCKED = new Set(BLOCKED_SLUGS.map((s) => s.toLowerCase()));

/** True for a product lab must not surface (see config BLOCKED_SLUGS). Keyed on
 *  slug — the identifier every normalized entry carries. */
export function isBlocked(entry: Pick<Entry, 'slug'>): boolean {
  return BLOCKED.has(entry.slug.trim().toLowerCase());
}

/** Drop blocked products. Applied to freshly-fetched AND cached entries so a
 *  stale cache can never resurrect a removed product. */
export function filterBlocked<T extends Pick<Entry, 'slug'>>(entries: T[]): T[] {
  return entries.filter((e) => !isBlocked(e));
}

/** Normalize a raw index entry; returns null when it's too malformed to show. */
export function normalizeEntry(raw: RawIndexEntry): Entry | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const slug = typeof raw.slug === 'string' ? raw.slug.trim() : '';
  if (!name || !slug) return null;
  const type = TYPES.has(raw.type ?? '') ? (raw.type as Entry['type']) : 'tool';
  const tags = Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [];
  const url = typeof raw.url === 'string' && raw.url.startsWith('http') ? raw.url : null;
  const category = resolveCategory(raw.category, tags, type);
  let host: string | null = null;
  if (url) {
    try {
      host = new URL(url).host;
    } catch {
      host = null;
    }
  }
  const country =
    typeof raw.country === 'string' && /^[A-Za-z]{2}$/.test(raw.country.trim())
      ? raw.country.trim().toUpperCase()
      : null;
  return {
    name,
    slug,
    description: typeof raw.description === 'string' ? raw.description : '',
    url,
    repo: typeof raw.repo === 'string' ? raw.repo : null,
    tags,
    type,
    date: typeof raw.date === 'string' ? raw.date : '1970-01-01',
    // icon is a Lucide icon name; legacy emoji values (or unknown names) fall
    // back to the category icon so stale indexes degrade gracefully.
    icon:
      typeof raw.icon === 'string' && hasIcon(raw.icon.trim())
        ? raw.icon.trim()
        : metaFor(category).icon,
    category,
    country,
    host,
    stats: null,
  };
}

/**
 * Deduplicate entries that appear in both indexes (e.g. dropwell).
 * Keyed by repo (lowercased) falling back to slug; prefers the tool-factory
 * entry when both exist.
 */
export function dedupeEntries(entries: Entry[]): Entry[] {
  const byKey = new Map<string, Entry>();
  for (const e of entries) {
    const key = (e.repo ?? e.slug).toLowerCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, e);
    } else if (existing.type !== 'tool' && e.type === 'tool') {
      byKey.set(key, e);
    }
  }
  return [...byKey.values()];
}

/** Attach analytics stats to entries by hostname. Mutates copies, returns new array.
 *  Products never get stats, whatever the analytics file holds: their analytics
 *  are never shown. */
export function attachStats(entries: Entry[], analytics: AnalyticsFile | null): Entry[] {
  if (!analytics || !analytics.hosts) return entries;
  return entries.map((e) => {
    if (!e.host || e.type === 'product') return e;
    const stats = analytics.hosts[e.host];
    return stats ? { ...e, stats } : e;
  });
}

/** Lowercased "owner/name" from a repo URL, or null when it isn't parseable. */
export function repoKey(repo: string | null): string | null {
  if (!repo) return null;
  try {
    const parts = new URL(repo).pathname
      .replace(/^\/+/, '')
      .replace(/\.git$/, '')
      .split('/');
    return parts.length >= 2 ? `${parts[0]}/${parts[1]}`.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Attach each entry's repo push + creation times by matching its repo URL.
 *  pushedAt powers the UPDATED badge; createdAt powers the NEW badge. */
export function attachPushes(entries: Entry[], meta: Map<string, RepoMeta>): Entry[] {
  if (meta.size === 0) return entries;
  return entries.map((e) => {
    const key = repoKey(e.repo);
    const m = key ? meta.get(key) : undefined;
    return m ? { ...e, pushedAt: m.pushedAt, createdAt: m.createdAt } : e;
  });
}
